import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// API Base URL
const API_BASE_URL = (
  process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api/v1'
).replace(/\/$/, '');

// Async thunks for social accounts
export const getSocialAccountsAPI = createAsyncThunk(
  'socialAccounts/getSocialAccounts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/social-accounts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch social accounts');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const linkSocialAccountAPI = createAsyncThunk(
  'socialAccounts/linkSocialAccount',
  async (accountData, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/social-accounts/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(accountData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to link social account');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const unlinkSocialAccountAPI = createAsyncThunk(
  'socialAccounts/unlinkSocialAccount',
  async (accountId, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/social-accounts/unlink/${accountId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to unlink social account');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkProviderLinkedAPI = createAsyncThunk(
  'socialAccounts/checkProviderLinked',
  async (provider, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/social-accounts/check/${provider}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to check provider status');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  socialAccounts: [],
  isLoading: false,
  isLinking: false,
  isUnlinking: false,
  error: null,
  message: null,
  showConfirmationModal: false,
  confirmationData: null
};

// Social accounts slice
const socialAccountsSlice = createSlice({
  name: 'socialAccounts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    hideConfirmationModal: (state) => {
      state.showConfirmationModal = false;
      state.confirmationData = null;
    },
    showUnlinkConfirmation: (state, action) => {
      state.showConfirmationModal = true;
      state.confirmationData = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get social accounts
      .addCase(getSocialAccountsAPI.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSocialAccountsAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.socialAccounts = action.payload.data || [];
        state.error = null;
      })
      .addCase(getSocialAccountsAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Link social account
      .addCase(linkSocialAccountAPI.pending, (state) => {
        state.isLinking = true;
        state.error = null;
      })
      .addCase(linkSocialAccountAPI.fulfilled, (state, action) => {
        state.isLinking = false;
        state.message = action.payload.message;
        state.error = null;
        // Refresh the list by triggering getSocialAccountsAPI
      })
      .addCase(linkSocialAccountAPI.rejected, (state, action) => {
        state.isLinking = false;
        state.error = action.payload;
      })
      
      // Unlink social account
      .addCase(unlinkSocialAccountAPI.pending, (state) => {
        state.isUnlinking = true;
        state.error = null;
      })
      .addCase(unlinkSocialAccountAPI.fulfilled, (state, action) => {
        state.isUnlinking = false;
        state.message = action.payload.message;
        state.error = null;
        state.showConfirmationModal = false;
        state.confirmationData = null;
        // Remove the unlinked account from the list
        const accountId = state.confirmationData?.id;
        if (accountId) {
          state.socialAccounts = state.socialAccounts.filter(account => account.id !== accountId);
        }
      })
      .addCase(unlinkSocialAccountAPI.rejected, (state, action) => {
        state.isUnlinking = false;
        state.error = action.payload;
        state.showConfirmationModal = false;
        state.confirmationData = null;
      })
      
      // Check provider linked
      .addCase(checkProviderLinkedAPI.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkProviderLinkedAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(checkProviderLinkedAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const {
  clearError,
  clearMessage,
  hideConfirmationModal,
  showUnlinkConfirmation
} = socialAccountsSlice.actions;

export default socialAccountsSlice.reducer;
